import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { useSnackbar } from 'notistack';
import { AxiosError } from 'axios';

import Page from '../../components/Layout/Page';
import useStyles from './styles';
import Header from './components/Header';
import FormContent from './components/FormContent';
import Logo from '../../components/Layout/Logo';
import SuccessModal from './components/SuccessModal';
import { EnrollmentFormData } from './types';
import { validateStep, validateAllSteps, ValidationError } from './validations';
import { getEnrollmentSteps, StepConfig } from './formConfig';
import { postCreateStudentWithRepresentatives } from '../../requests/api/students';
import { getSubjects } from '../../requests/api/subjects';
import { getLocations } from '../../requests/api/sections';
import { getSchools, postIncompleteStudent } from '../../requests/api/enrollment';
import handleApiResponse from '../../utils/handleApiResponse';

function StudentEnrollment() {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [enrollmentSteps, setEnrollmentSteps] = useState<StepConfig[]>([]);
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [formData, setFormData] = useState<EnrollmentFormData>({
    name: '',
    lastname: '',
    motherLastname: '',
    rut: '',
    birthDate: '',
    email: '',
    phoneNumber: '',
    schoolId: 0,
    otherSchoolName: '',

    selectedSubjects: [],
    testsComment: '',

    preferredLocationIds: [],
    desiredClassYear: '',
    inscriptionYear: null,

    representativeName: '',
    representativeLastname: '',
    representativeMotherLastname: '',
    representativeRut: '',
    representativeBirthDate: '',
    representativeEmail: '',
    representativeNationality: '',
    representativeMaritalStatus: '',
    representativePhoneNumber: '',
    representativeAddress: '',
    representativeCommune: '',
    representativeOtherCommune: '',

    isSameAsRepresentative: false,
    supporterName: '',
    supporterLastname: '',
    supporterMotherLastname: '',
    supporterRut: '',
    supporterBirthDate: '',
    supporterEmail: '',
    supporterNationality: '',
    supporterMaritalStatus: '',
    supporterPhoneNumber: '',
    supporterAddress: '',
    supporterCommune: '',
    supporterOtherCommune: '',

    leadSource: '',
    leadName: '',
    ambassadorCode: '',
  });

  const totalSteps = 6;

  useEffect(() => {
    const fetchSubjectsLocationsAndSchools = async () => {
      try {
        const [subjectsResp, locationsResp, schoolsResp] = await Promise.all([
          getSubjects(),
          getLocations(),
          getSchools(),
        ]);

        const subjects = (subjectsResp.subjects || []).map((s: {
          id: string | number;
          name: string;
        }) => ({
          id: String(s.id),
          name: s.name,
        }));

        const locationOptions = (locationsResp.locations || [])
          .filter((loc: {
            id: string | number;
            name: string;
          }) => !loc.name.toLowerCase().includes('online'))
          .map((loc: {
            id: string | number;
            name: string;
          }) => ({
            label: loc.name,
            value: String(loc.id),
          }));

        const schoolOptions = (schoolsResp.schools || []).map((school: {
          id: string | number;
          name: string;
        }) => ({
          label: school.name,
          value: String(school.id),
        }));

        setEnrollmentSteps(getEnrollmentSteps(subjects, locationOptions, schoolOptions));
      } catch (err) {
        const error = err as AxiosError;
        handleApiResponse(enqueueSnackbar, error, false);
      }
    };

    fetchSubjectsLocationsAndSchools();
  }, []);

  const handleNext = async () => {
    const validation = validateStep(currentStep, formData, enrollmentSteps);

    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      return;
    }

    setValidationErrors([]);
    if (currentStep === 1) {
      try {
        const step1 = enrollmentSteps.find((s) => s.stepNumber === 1);
        const schoolField = step1?.fields.find((f) => f.name === 'schoolId');
        const selectedSchoolOption = schoolField?.options?.find(
          (o) => String(o.value) === String(formData.schoolId),
        );
        const isOtherSchool = selectedSchoolOption?.label === 'Otro';

        await postIncompleteStudent({
          rut: formData.rut,
          name: formData.name,
          lastname: formData.lastname,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          schoolId: formData.schoolId ? String(formData.schoolId) : undefined,
          otherSchoolName: isOtherSchool ? formData.otherSchoolName || null : null,
          birthDate: formData.birthDate || null,
          preferredLocationIds: formData.preferredLocationIds.length > 0
            ? formData.preferredLocationIds.map((id) => String(id))
            : null,
          desiredClassYear: formData.desiredClassYear || null,
          inscriptionYear: formData.inscriptionYear ?? null,
          leadSource: formData.leadSource || null,
          leadName: formData.leadName || null,
          ambassadorCode: formData.ambassadorCode || null,
        });
      } catch (err) {
        const error = err as AxiosError;
        handleApiResponse(enqueueSnackbar, error, false);
        return;
      }
    }

    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    setValidationErrors([]);

    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFormChange = (data: Partial<EnrollmentFormData>) => {
    let updatedData = { ...data };

    Object.keys(data).forEach((key) => {
      if (key.startsWith('subject_')) {
        const subjectId = key.replace('subject_', '');
        const isSelected = data[key as keyof EnrollmentFormData] as boolean;

        if (isSelected && !formData.selectedSubjects.includes(subjectId)) {
          updatedData.selectedSubjects = [...formData.selectedSubjects, subjectId];
        } else if (!isSelected && formData.selectedSubjects.includes(subjectId)) {
          updatedData.selectedSubjects = formData.selectedSubjects.filter((id) => id !== subjectId);
        }
      }
    });

    if (data.isSameAsRepresentative === true) {
      updatedData = {
        ...updatedData,
        supporterName: formData.representativeName,
        supporterLastname: formData.representativeLastname,
        supporterMotherLastname: formData.representativeMotherLastname,
        supporterRut: formData.representativeRut,
        supporterBirthDate: formData.representativeBirthDate,
        supporterEmail: formData.representativeEmail,
        supporterNationality: formData.representativeNationality,
        supporterMaritalStatus: formData.representativeMaritalStatus,
        supporterPhoneNumber: formData.representativePhoneNumber,
        supporterAddress: formData.representativeAddress,
        supporterCommune: formData.representativeCommune,
        supporterOtherCommune: formData.representativeOtherCommune,
      };
    }
    if (data.isSameAsRepresentative === false) {
      updatedData = {
        ...updatedData,
        supporterName: '',
        supporterLastname: '',
        supporterMotherLastname: '',
        supporterRut: '',
        supporterBirthDate: '',
        supporterEmail: '',
        supporterNationality: '',
        supporterMaritalStatus: '',
        supporterPhoneNumber: '',
        supporterAddress: '',
        supporterCommune: '',
        supporterOtherCommune: '',
      };
    }

    // Limpiar campos solo cuando leadSource cambia
    if ('leadSource' in data && data.leadSource !== formData.leadSource) {
      if (data.leadSource !== 'embajador' && data.leadSource !== 'centro_alumnos') {
        updatedData = {
          ...updatedData,
          ambassadorCode: '',
        };
      }

      if (data.leadSource !== 'hermano' && data.leadSource !== 'profesor') {
        updatedData = {
          ...updatedData,
          leadName: '',
        };
      }
    }

    if ('inscriptionYear' in data) {
      const rawYear = data.inscriptionYear;
      const parsedYear = rawYear === null || rawYear === undefined
        ? null
        : Number(rawYear);
      updatedData.inscriptionYear = Number.isNaN(parsedYear as number) ? null : parsedYear;
    }

    setFormData((prev) => ({
      ...prev,
      ...updatedData,
    }));

    if (validationErrors.length > 0) {
      const fieldNames = Object.keys(updatedData);
      const remainingErrors = validationErrors.filter(
        (error) => !fieldNames.includes(error.field),
      );
      setValidationErrors(remainingErrors);
    }
  };

  const transformFormDataToApiRequest = (
    data: EnrollmentFormData,
    steps: StepConfig[],
  ) => {
    const step1 = steps.find((s) => s.stepNumber === 1);
    const schoolField = step1?.fields.find((f) => f.name === 'schoolId');
    const selectedSchoolOption = schoolField?.options?.find(
      (o) => String(o.value) === String(data.schoolId),
    );
    const isOtherSchool = selectedSchoolOption?.label === 'Otro';

    return {
      student: {
        phoneNumber: data.phoneNumber,
        rut: data.rut,
        name: data.name,
        lastname: `${data.lastname} ${data.motherLastname}`,
        email: data.email,
        birthDate: data.birthDate || undefined,
        stage: 'enrollment',
        schoolId: data.schoolId || null,
        otherSchoolName: isOtherSchool ? data.otherSchoolName || null : null,
        testsToTake: data.selectedSubjects,
        testsComment: data.testsComment || undefined,
        preferredLocationIds: data.preferredLocationIds,
        desiredClassYear: data.desiredClassYear,
        inscriptionYear: data.inscriptionYear ?? null,
        leadSource: data.leadSource,
        leadName: data.leadName || undefined,
        ambassadorCode: data.ambassadorCode || undefined,
      },
      representative: {
        phoneNumber: data.representativePhoneNumber,
        rut: data.representativeRut,
        name: data.representativeName,
        lastname: data.representativeLastname,
        email: data.representativeEmail,
        birthDate: data.representativeBirthDate || undefined,
        nationality: data.representativeNationality || undefined,
        maritalStatus: data.representativeMaritalStatus || undefined,
        address: data.representativeAddress || undefined,
        commune: data.representativeCommune || undefined,
        otherCommune: (() => {
          const step4 = steps.find((s) => s.stepNumber === 4);
          const communeField = step4?.fields.find((f) => f.name === 'representativeCommune');
          const selectedOption = communeField?.options?.find(
            (o) => String(o.value) === String(data.representativeCommune),
          );
          return selectedOption?.label === 'Otro' ? data.representativeOtherCommune || null : null;
        })(),
      },
      sustainer: {
        phoneNumber: data.supporterPhoneNumber,
        rut: data.supporterRut,
        name: data.supporterName,
        lastname: data.supporterLastname,
        email: data.supporterEmail,
        birthDate: data.supporterBirthDate || undefined,
        nationality: data.supporterNationality || undefined,
        maritalStatus: data.supporterMaritalStatus || undefined,
        address: data.supporterAddress || undefined,
        commune: data.supporterCommune || undefined,
        otherCommune: (() => {
          const step5 = steps.find((s) => s.stepNumber === 5);
          const communeField = step5?.fields.find((f) => f.name === 'supporterCommune');
          const selectedOption = communeField?.options?.find(
            (o) => String(o.value) === String(data.supporterCommune),
          );
          return selectedOption?.label === 'Otro' ? data.supporterOtherCommune || null : null;
        })(),
      },
    };
  };

  const handleSubmit = async () => {
    const validation = validateAllSteps(formData, enrollmentSteps);

    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      // eslint-disable-next-line no-console
      console.log('Validation errors:', validation.errors);
      return;
    }

    setValidationErrors([]);

    try {
      const requestData = transformFormDataToApiRequest(formData, enrollmentSteps);
      await postCreateStudentWithRepresentatives(requestData);

      setShowSuccessModal(true);
    } catch (err) {
      const error = err as AxiosError;
      handleApiResponse(enqueueSnackbar, error, false);
    }
  };

  const handleCloseSuccessModal = () => {
    window.location.href = '/login';
  };

  return (
    <Page
      title='Inscripción'
      className={classes.root}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        className={classes.logoContainer}
      >
        <Logo className={classes.logo} />
      </Box>
      <Box className={classes.mainContainer}>
        <Header
          currentStep={currentStep}
          totalSteps={totalSteps}
        />
        <FormContent
          currentStep={currentStep}
          formData={formData}
          onFormChange={handleFormChange}
          onNext={handleNext}
          onBack={handleBack}
          onSubmit={handleSubmit}
          totalSteps={totalSteps}
          validationErrors={validationErrors}
          enrollmentSteps={enrollmentSteps}
        />
      </Box>

      <SuccessModal
        open={showSuccessModal}
        onClose={handleCloseSuccessModal}
      />
    </Page>
  );
}

export default StudentEnrollment;
